import { useCallback, useEffect, useRef, useState } from "react";
import { getCtx, soundOn } from "@/lib/sfx";
import { analyzeTempo } from "@/lib/tempo";

// Dance on the song's real tempo: beats come from the detected BPM grid aligned to audio.currentTime.
export const useDance = (onBeat) => {
  const [dancing, setDancing] = useState(false);
  const [bpm, setBpm] = useState(null);
  const audio = useRef(null);
  const raf = useRef(0);
  const timer = useRef(0);
  const run = useRef(0);
  const beatRef = useRef(onBeat);
  beatRef.current = onBeat;

  const stop = useCallback(() => {
    run.current += 1;
    cancelAnimationFrame(raf.current);
    clearInterval(timer.current);
    audio.current?.pause();
    setDancing(false);
    setBpm(null);
  }, []);

  useEffect(() => () => stop(), [stop]);

  const start = useCallback((url, known) => {
    stop();
    const id = run.current;
    setDancing(true);
    if (!soundOn() || !url) {
      let i = 0;
      timer.current = setInterval(() => { beatRef.current?.(i++, 0.5); }, 500);
      setTimeout(() => { if (run.current === id) stop(); }, 20000);
      return;
    }
    const ctx = getCtx();
    if (!audio.current) {
      const a = new Audio();
      a.crossOrigin = "anonymous";
      a.addEventListener("ended", stop);
      audio.current = a;
      if (ctx) ctx.createMediaElementSource(a).connect(ctx.destination);
    }
    const a = audio.current;
    a.src = url;
    let grid = null;
    let lastIdx = -1;
    let lastFallback = performance.now();
    const loop = (now) => {
      if (run.current !== id) return;
      if (grid && !a.paused) {
        const idx = Math.floor((a.currentTime - grid.offset) / grid.period);
        if (idx > lastIdx && idx >= 0) {
          lastIdx = idx;
          beatRef.current?.(idx, grid.period);
        }
      } else if (now - lastFallback > 500) {
        lastFallback = now;
        beatRef.current?.(Math.floor(now / 500), 0.5);
      }
      raf.current = requestAnimationFrame(loop);
    };
    const begin = (g) => {
      if (run.current !== id) return;
      grid = g;
      if (g) setBpm(g.bpm);
      a.currentTime = 0;
      a.play().catch(() => {});
    };
    raf.current = requestAnimationFrame(loop);
    if (known?.period) {
      begin(known);
      return;
    }
    if (!ctx) {
      begin(null);
      return;
    }
    Promise.race([analyzeTempo(url, ctx), new Promise((r) => setTimeout(() => r(null), 4000))])
      .then(begin)
      .catch(() => begin(null));
  }, [stop]);

  return { dancing, bpm, start, stop };
};
